import { IMAGE_CONSTANTS, EVENT_CONSTANTS, VALIDATION_PATTERNS } from './constants';

export const parseDate = (dateString) => {
  if (!dateString) return null;
  
  const months = {
    'enero': '01', 'febrero': '02', 'marzo': '03', 'abril': '04',
    'mayo': '05', 'junio': '06', 'julio': '07', 'agosto': '08',
    'septiembre': '09', 'octubre': '10', 'noviembre': '11', 'diciembre': '12'
  };
  
  const regex = /(\d{2})\s+de\s+(\w+)\s+del\s+(\d{4})/;
  const match = dateString.match(regex);
  
  if (match) {
    const [, day, month, year] = match;
    const monthNumber = months[month.toLowerCase()];
    return new Date(`${year}-${monthNumber}-${day}T12:00:00`);
  }
  
  let dateStr = dateString;
  if (!dateString.includes('T') && !dateString.includes(' ')) {
    dateStr = dateString + "T12:00:00";
  }
  return new Date(dateStr);
};

export const validateImageFile = (file) => {
  const errors = [];
  
  if (!file) {
    errors.push('No se seleccionó ningún archivo');
    return { isValid: false, errors };
  }
  
  if (!IMAGE_CONSTANTS.ALLOWED_TYPES.includes(file.type)) {
    errors.push(`Tipo de archivo no permitido: ${file.type}`);
  }
  
  if (file.size > IMAGE_CONSTANTS.MAX_FILE_SIZE) {
    errors.push(`El archivo es demasiado grande: ${(file.size / 1024 / 1024).toFixed(2)}MB (máximo 5MB)`);
  }
  
  if (file.name.length > 255) {
    errors.push('El nombre del archivo es demasiado largo');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateEventData = (eventData) => {
  const errors = [];
  const warnings = [];
  
  if (!eventData.titulo || eventData.titulo.trim().length === 0) {
    errors.push('El título es requerido');
  } else {
    if (eventData.titulo.length < EVENT_CONSTANTS.MIN_TITLE_LENGTH) {
      errors.push('El título es demasiado corto');
    }
    
    if (eventData.titulo.length > EVENT_CONSTANTS.MAX_TITLE_LENGTH) {
      errors.push(`El título excede el máximo de ${EVENT_CONSTANTS.MAX_TITLE_LENGTH} caracteres`);
    }
    
    if (!VALIDATION_PATTERNS.ALPHANUMERIC.test(eventData.titulo)) {
      warnings.push('El título contiene caracteres especiales no recomendados');
    }
  }
  
  if (!eventData.ubicacion || eventData.ubicacion.trim().length === 0) {
    errors.push('La ubicación es requerida');
  } else {
    if (eventData.ubicacion.length < EVENT_CONSTANTS.MIN_LOCATION_LENGTH) {
      errors.push('La ubicación es demasiado corta');
    }
    
    if (eventData.ubicacion.length > EVENT_CONSTANTS.MAX_LOCATION_LENGTH) {
      errors.push(`La ubicación excede el máximo de ${EVENT_CONSTANTS.MAX_LOCATION_LENGTH} caracteres`);
    }
  }
  
  if (!eventData.fechaInicio) {
    errors.push('La fecha de inicio es requerida');
  } else {
    const inicio = new Date(eventData.fechaInicio);
    if (isNaN(inicio.getTime())) {
      errors.push('La fecha de inicio no es válida');
    }
  }
  
  if (!eventData.fechaFin) {
    errors.push('La fecha de fin es requerida');
  } else {
    const fin = new Date(eventData.fechaFin);
    if (isNaN(fin.getTime())) {
      errors.push('La fecha de fin no es válida');
    }
  }
  
  if (eventData.fechaInicio && eventData.fechaFin) {
    const inicio = new Date(eventData.fechaInicio);
    const fin = new Date(eventData.fechaFin);
    
    if (fin <= inicio) {
      errors.push('La fecha de fin debe ser posterior a la fecha de inicio');
    }
    
    const ahora = new Date();
    if (inicio < ahora) {
      warnings.push('La fecha de inicio es anterior a la fecha actual');
    }
  }
  
  if (eventData.imagenUrl && typeof eventData.imagenUrl !== 'string') {
    errors.push('La URL de la imagen debe ser un texto válido');
  }
  
  if (eventData.imagenUrl && !isValidUrl(eventData.imagenUrl)) {
    warnings.push('La URL de la imagen no parece ser válida');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

export const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

export const formatDateForAPI = (date, type = 'start') => {
  if (!date) return null;
  
  const d = parseDate(date);
  if (!d || isNaN(d.getTime())) return null;
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const time = type === 'end' ? '23:59:59' : '06:00:00';
  
  return `${year}-${month}-${day} ${time}`;
};

export const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString + "T12:00:00");
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return dateString;
  }
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getProgressPercentage = (loaded, total) => {
  if (!total || total === 0) return 0;
  return Math.round((loaded / total) * 100);
};

export const generateUniqueFileName = (originalName) => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  const extension = originalName.split('.').pop();
  const nameWithoutExtension = originalName.split('.').slice(0, -1).join('.');
  
  return `${nameWithoutExtension}_${timestamp}_${random}.${extension}`;
};

export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};